import { zodResolver } from "@hookform/resolvers/zod"
import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { AdminOnly } from "modules/shared/components/AdminOnly"
import { useAmphoraIntegrationQuery } from "modules/settings/hooks/amphora/useAmphoraIntegrationQuery"
import { useCreateAmphoraIntegrationConfigurationMutation } from "modules/settings/hooks/amphora/useCreateAmphoraIntegrationConfigurationMutation"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpsertAmphoraIntegration } from "schema"
import { z } from "zod"
import { DeleteAmphoraIntegration } from "./DeleteAmphoraIntegration"

export const AmphoraAuthModal = () => {
  const { t } = useTranslation()
  const form = useForm<z.infer<typeof UpsertAmphoraIntegration>>({
    resolver: zodResolver(UpsertAmphoraIntegration),
  })
  const navigate = useNavigate()
  const { mutateAsync, isPending } = useCreateAmphoraIntegrationConfigurationMutation()
  const { data } = useAmphoraIntegrationQuery()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const onSubmit = async (data: z.infer<typeof UpsertAmphoraIntegration>) => {
    await mutateAsync(data)
    onOpenChange()
  }

  const onOpenChange = (isOpen: boolean = false) => {
    if (!isOpen) {
      navigate({ to: "/settings/integrations/amphora" })
    }
  }

  useEffect(() => {
    if (data) {
      form.reset(data)
    }
  }, [data, form])

  return (
    <>
      <Dialog open onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("settings.integrations.amphora.authModal.title")}</DialogTitle>
            <DialogDescription>
              {t("settings.integrations.amphora.authModal.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <Form
              {...form}
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
              errorTranslations={getErrorTranslations()}
            >
              <div className="flex flex-col w-full gap-4">
                <FormField
                  control={form.control}
                  name="accessId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("settings.integrations.amphora.authModal.form.accessId")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} required />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                {data?.accessId ? (
                  <AdminOnly superadminOnly>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      {t("settings.integrations.amphora.integrationCard.deleteIntegration.button")}
                    </Button>
                  </AdminOnly>
                ) : null}
                <Button disabled={form.formState.isLoading || !form.formState.isValid || isPending}>
                  {t("settings.integrations.amphora.authModal.form.submit")}
                </Button>
              </DialogFooter>
            </Form>
          </div>
        </DialogContent>
      </Dialog>
      <DeleteAmphoraIntegration
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDeleted={() => onOpenChange()}
      />
    </>
  )
}
